import { useState } from 'react'
import { toast } from 'react-toastify'
import { useParams } from 'react-router-dom'
import Countdown, { zeroPad } from 'react-countdown'
import ToastMessage from '../ToastMessage'
import LockInfo from '../../type/LockInfo'
import { formatNumber } from '../../utils'
import { StyledWithdrawRow, WithdrawAmount, WithdrawAction, WithdrawCountdown, WithdrawSpan } from './Styled'
import { useActiveWeb3React, useTokenContract } from '../../hooks'
import config from '../../config.json'

interface IWithdrawRowProps {
  lockInfo: LockInfo
  onRefresh: () => void
}

function WithdrawRow(props: IWithdrawRowProps): JSX.Element {
  const { slug } = useParams()
  const { lockInfo, onRefresh } = props

  const [isWithdrawing, setWithdrawing] = useState(false)

  const { account, chainId } = useActiveWeb3React()
  const networkId = chainId ?? Number(process.env.REACT_APP_CHAIN_ID)
  const pools = config.pools[networkId]
  // @ts-ignore
  const pool = pools.find(p => p.slug === slug)
  const tokenContract = useTokenContract(pool?.contract)

  const onWithdraw = async index => {
    try {
      setWithdrawing(true)

      if (account && tokenContract) {
        const receipt = await tokenContract.methods.getUnlock(account, index).send({
          from: account,
        })

        if (receipt) {
          onRefresh()

          toast.success(
            <ToastMessage
              color="success"
              headerText="Success!"
              bodyText="Withdraw success!"
              link={`${process.env.REACT_APP_EXPLORER_URL}/tx/${receipt.transactionHash}`}
              linkText="View Transaction"
            />,
            {
              toastId: 'onWithdraw',
            },
          )
        }
      }
    } catch (error: any) {
      // we only care if the error is something _other_ than the user rejected the tx
      if (error?.code !== 4001) {
        toast.error(
          <ToastMessage color="error" headerText="Error!" bodyText="Could not withdraw. Please try again." />,
          {
            toastId: 'onWithdraw',
          },
        )
      }
      console.error(error)
    } finally {
      setWithdrawing(false)
    }
  }

  return (
    <StyledWithdrawRow>
      <WithdrawAmount>{`${formatNumber(lockInfo.amount.toFixed(3))}`}</WithdrawAmount>
      <WithdrawAction>
        <Countdown
          date={lockInfo.unlockableAt * 1000}
          zeroPadTime={2}
          renderer={props2 =>
            props2.completed ? (
              <WithdrawSpan onClick={() => onWithdraw(lockInfo.index)}>
                {isWithdrawing ? 'Confirming...' : 'Withdraw'}
              </WithdrawSpan>
            ) : (
              <WithdrawCountdown>
                {zeroPad(props2.days)}d:{zeroPad(props2.hours)}h:{zeroPad(props2.minutes)}m:{zeroPad(props2.seconds)}s
              </WithdrawCountdown>
            )
          }
        />
      </WithdrawAction>
    </StyledWithdrawRow>
  )
}

export default WithdrawRow
